import { ColorNeutralGrey4, ColorNeutralWhite, Spacing12, Spacing16 } from 'autogen/design-tokens/tokens';
import Checkbox from 'components/shared/inputs/checkbox';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import type { ProductRegimeProductModel } from 'autogen/swagger/all';
import Card from 'components/generic/card';
import { useTranslation } from 'react-i18next';
import * as transKey from 'autogen/translation-keys/trans-root-website';
import ProductItem from 'components/shared/productItem';
import { Typography } from 'components/generic/typography';
import { StockNotificationButtonCompact } from 'components/generic/productCTA/components/OpenStockNotification/StockNotificationButton';
import { firstCharacterToLower } from 'modules/helpers/strings';
import { createProductItemTrackingContext } from 'components/shared/productItem/shared/helpers';
import { useRegime } from './Regime';
import RegimeVariantSelector from './RegimeVariantSelector';
import { overlayColor } from 'components/shared/style/imageOverlay';

/* #region  styles */
const StyledCard = styled(Card)<{ addHeaderSpacing: boolean }>`
    border: 1px solid ${ColorNeutralGrey4};

    ${({ addHeaderSpacing }) =>
        addHeaderSpacing &&
        css`
            margin-top: 30.5px;
        `}
`;

const StyledCardHeader = styled(Card.Header)`
    justify-content: center;
    background-color: ${overlayColor};
`;

const StyledCardContent = styled(Card.Content)`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
`;

const StyledCardFooter = styled(Card.Footer)`
    display: block;
    background-color: ${ColorNeutralWhite};
    padding: 0 ${Spacing16} ${Spacing16};
    height: 56px;
`;

const CheckboxContainer = styled.label`
    display: flex;
    position: absolute;
    right: ${Spacing16};
    top: ${Spacing16};
    z-index: 2;
`;

const StyledProductItem = styled(ProductItem)<{ isSoldOut: boolean }>`
    height: 100%;

    ${({ isSoldOut }) =>
        isSoldOut &&
        css`
            opacity: 0.4;
        `}
`;

const StyledButton = styled(StockNotificationButtonCompact)`
    position: absolute;
    right: ${Spacing16};
    bottom: ${Spacing16};
    width: 40px;
`;

/* #endregion */

export default function RegimeCardVertical({ product }: { product: ProductRegimeProductModel }) {
    const { product: productItem, variants, stockAvailable } = product;
    const {
        isSelected,
        toggleProduct,
        mainProductRegimeId,
        excludeMainProduct,
        hasProductWithVariants,
        deselectProduct
    } = useRegime();
    const selected = isSelected(productItem.id);
    const { t } = useTranslation();
    const isCurrentProduct = productItem.id === mainProductRegimeId;
    const showFooter = hasProductWithVariants();
    const trackingContext = createProductItemTrackingContext(firstCharacterToLower(productItem.uaTrackingCategory));

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!stockAvailable) {
            deselectProduct(productItem.id);
        }
    }, []);

    return (
        <StyledCard addHeaderSpacing={!isCurrentProduct && !excludeMainProduct}>
            {isCurrentProduct && (
                <StyledCardHeader>
                    <Typography.Body size="S" strong as="span">
                        {t(transKey.ProductRegime.CurrentProduct)}
                    </Typography.Body>
                </StyledCardHeader>
            )}

            <StyledCardContent>
                <StyledProductItem model={productItem} themeType="regime" isSoldOut={!stockAvailable} />

                {!stockAvailable && (
                    <StyledButton
                        params={{
                            action: productItem.action,
                            trackingContext,
                            productId: productItem.id,
                            externalProductId: productItem.externalProductId,
                            productNavigationLink: productItem.linkAction,
                            fromPowerstep: false,
                            showPowerstepOnAddToBasket: false,
                            addToBasketType: productItem.addToBasketType
                        }}
                        trackingInfo={trackingContext}
                    />
                )}

                <CheckboxContainer>
                    <Checkbox
                        value={productItem.id}
                        checked={selected && stockAvailable}
                        disabled={!stockAvailable}
                        onChange={() => toggleProduct(productItem.id)}
                    />
                </CheckboxContainer>
            </StyledCardContent>

            {showFooter && (
                <StyledCardFooter>
                    {variants.length > 1 && <RegimeVariantSelector product={product} />}
                </StyledCardFooter>
            )}
        </StyledCard>
    );
}
